import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { saveAppointment } from '../../services/firebase/appointmentService';
import { getActiveTreatments } from '../../services/firebase/treatmentService';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// A date input's "YYYY-MM-DD" value is a plain calendar date with no timezone.
// Read its weekday in UTC with no hour offset, so the visitor's timezone can
// never shift it to the previous or next day.
const getDayOfWeek = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return DAY_NAMES[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
};

// Today's date as "YYYY-MM-DD" in the visitor's local time (toISOString() is
// UTC, which rolls over to tomorrow in the evening in Texas).
const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const Appointment = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    treatment: '',
    message: ''
  });
  
  const [validated, setValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  useEffect(() => {
    const loadTreatments = async () => {
      try {
        setLoading(true);
        const activeTreatments = await getActiveTreatments();
        
        if (activeTreatments && activeTreatments.length > 0) {
          setTreatments(activeTreatments);
        } else {
          // Fallback treatments if none are fetched from Firebase
          const fallbackTreatments = [
            {
              id: '1',
              name: 'DOT Physical Exam',
              description: 'Department of Transportation physical examination',
              availability: 'Monday and Wednesday, 9:30 AM to 5:00 PM',
              availableDays: ['Monday', 'Wednesday'],
              availableTimes: ['09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM'],
              active: true
            },
            {
              id: '2',
              name: 'HRT',
              description: 'Hormone Replacement Therapy',
              availability: 'Monday to Wednesday, 9:00 AM to 5:00 PM',
              availableDays: ['Monday', 'Tuesday', 'Wednesday'],
              availableTimes: ['09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM'],
              active: true
            }
          ];
          setTreatments(fallbackTreatments);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Appointment.js: Error loading treatments:', err);
        setError('Failed to load treatment options. Please try again later.');
        setLoading(false);
      }
    };
    
    loadTreatments();
  }, []);

  // Auto-select treatment from URL query parameter (e.g., from Services page)
  useEffect(() => {
    const treatmentParam = searchParams.get('treatment');
    if (treatmentParam && treatments.length > 0) {
      // Find the treatment whose name matches the service title from the URL
      const matchingTreatment = treatments.find(
        t => t.name.trim().toLowerCase() === treatmentParam.trim().toLowerCase()
      );
      if (matchingTreatment) {
        setFormData(prev =>
          prev.treatment === matchingTreatment.id
            ? prev
            : { ...prev, treatment: matchingTreatment.id }
        );
        setSelectedTreatment(matchingTreatment);
      }
    }
  }, [searchParams, treatments]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // When treatment changes, update the selected treatment object
    if (name === 'treatment') {
      const treatment = treatments.find(t => t.id === value);
      setSelectedTreatment(treatment || null);
      
      // Clear date and time if treatment changes
      if (formData.date) {
        const dayOfWeek = getDayOfWeek(formData.date);

        // If the day is not available for this treatment, clear the date and time
        if (!treatment || !treatment.availableDays.includes(dayOfWeek)) {
          setFormData(prev => ({
            ...prev,
            date: '',
            time: ''
          }));
        }
      }
    }
  };

  // Function to check if a date should be disabled based on treatment availability
  const isDateDisabled = (dateString) => {
    if (!selectedTreatment || !selectedTreatment.availableDays) {
      return false; // If no treatment selected, don't disable any dates
    }
    return !selectedTreatment.availableDays.includes(getDayOfWeek(dateString));
  };
  
  // Helper function to format day name for error messages
  const formatDayName = (day) => {
    // Handle plural forms correctly
    if (day === 'Monday') return 'Mondays';
    if (day === 'Tuesday') return 'Tuesdays';
    if (day === 'Wednesday') return 'Wednesdays';
    if (day === 'Thursday') return 'Thursdays';
    if (day === 'Friday') return 'Fridays';
    if (day === 'Saturday') return 'Saturdays';
    if (day === 'Sunday') return 'Sundays';
    return day;
  };

  // e.g. "Cryo Body Sculpting (GLYPTO) is available on Mondays and Wednesdays. Please select one of those days."
  const availabilityMessage = (treatment) => {
    const name = (treatment.name || 'This service').trim();
    const days = [...(treatment.availableDays || [])]
      .sort((a, b) => DAY_NAMES.indexOf(a) - DAY_NAMES.indexOf(b))
      .map(formatDayName);
    if (days.length === 0) {
      return `${name} has no available days right now. Please call us at (432) 653-5487 to schedule.`;
    }
    const list = days.length === 1
      ? days[0]
      : `${days.slice(0, -1).join(', ')} and ${days[days.length - 1]}`;
    return `${name} is available on ${list}. Please select one of those days.`;
  };
  
  
  // Generate available time slots based on selected treatment and date
  const getAvailableTimeSlots = () => {
    if (!selectedTreatment || !selectedTreatment.availableTimes || !formData.date) {
      return [];
    }
    
    // Check if the selected date is available for this treatment
    if (!selectedTreatment.availableDays.includes(getDayOfWeek(formData.date))) {
      return [];
    }
    
    // Create a Set to track unique time strings and avoid duplicates
    const uniqueTimes = new Set();
    
    // Normalize and format times to ensure consistency with admin section
    return [...selectedTreatment.availableTimes]
      .map(time => {
        // Handle case where time might not have a space or period
        if (!time.includes(' ')) {
          // Try to parse as 24-hour format and convert to 12-hour
          const [hours, minutes] = time.split(':');
          const hour = parseInt(hours, 10);
          if (hour >= 0 && hour < 24) {
            const period = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM
            return `${hour12}:${minutes} ${period}`;
          }
          return time; // Return as is if parsing fails
        }
        return time;
      })
      .sort((a, b) => {
        // Extract hours, minutes, and period for comparison
        const [timeA, periodA] = a.split(' ');
        const [timeB, periodB] = b.split(' ');
        
        if (!timeA || !periodA || !timeB || !periodB) {
          console.error('Invalid time format:', a, b);
          return 0;
        }
        
        const [hoursA, minutesA] = timeA.split(':');
        const [hoursB, minutesB] = timeB.split(':');
        
        // Compare periods first (AM comes before PM)
        if (periodA !== periodB) {
          return periodA === 'AM' ? -1 : 1;
        }
        
        // For same period, compare hours
        const hourA = parseInt(hoursA, 10);
        const hourB = parseInt(hoursB, 10);
        
        // Special handling for 12 o'clock
        const normalizedHourA = hourA === 12 ? 0 : hourA;
        const normalizedHourB = hourB === 12 ? 0 : hourB;
        
        if (normalizedHourA !== normalizedHourB) {
          return normalizedHourA - normalizedHourB;
        }
        
        // If hours are equal, compare minutes
        return parseInt(minutesA, 10) - parseInt(minutesB, 10);
      })
      .map(time => {
        if (!time.includes(' ')) return time; // Skip invalid formats
        
        const [timeStr, period] = time.split(' ');
        if (!timeStr || !period) return time; // Skip invalid formats
        
        const [hours, minutes] = timeStr.split(':');
        if (!hours || !minutes) return time; // Skip invalid formats
        
        // Remove leading zero from hours (e.g., '09:30 AM' becomes '9:30 AM')
        const formattedHour = hours.startsWith('0') ? hours.substring(1) : hours;
        return `${formattedHour}:${minutes} ${period}`;
      })
      // Filter out duplicates
      .filter(time => {
        if (uniqueTimes.has(time)) {
          return false;
        }
        uniqueTimes.add(time);
        return true;
      });
  };

  const validateForm = (form) => {
    if (form.checkValidity() === false) {
      return false;
    }
    
    // Additional validation for treatment availability
    if (selectedTreatment && formData.date) {
      if (!selectedTreatment.availableDays.includes(getDayOfWeek(formData.date))) {
        setError(availabilityMessage(selectedTreatment));
        return false;
      }
    }
    
    return true;
  };

  const sendConfirmationEmail = (appointmentData) => {
    // Replace these with your actual EmailJS credentials
    const serviceId = 'YOUR_SERVICE_ID';
    const templateId = 'YOUR_TEMPLATE_ID';
    const publicKey = 'YOUR_PUBLIC_KEY';
    
    const templateParams = {
      name: appointmentData.name,
      recipient: appointmentData.email,
      date: appointmentData.date,
      time: appointmentData.time,
      treatment: selectedTreatment ? selectedTreatment.name : appointmentData.treatment
    };
    
    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        setEmailSent(true);
      })
      .catch((err) => {
        console.error('Failed to send email:', err);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    setValidated(true);
    setError('');
    
    if (!validateForm(form)) {
      e.stopPropagation();
      return;
    }
    
    try {
      // Save appointment to Firebase with treatment information instead of department/doctor
      await saveAppointment({
        ...formData,
        treatment: selectedTreatment ? selectedTreatment.name : '', // Use treatment name directly
        // Remove department and doctor fields
        status: 'Pending',
        createdAt: new Date().toISOString()
      });
      
      // Send confirmation email
      sendConfirmationEmail(formData);
      
      setSubmitted(true);
      setValidated(false); // Reset validation state on successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        treatment: '',
        message: ''
      });
      setSelectedTreatment(null);
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError('There was an error booking your appointment. Please try again later.');
    }
  };

  return (
    <>
      {/* Page Header */}
      <section className="page-section bg-light" style={{ padding: '56px 0 28px' }}>
        <Container>
          <div className="section-header">
            <h3>Book an Appointment</h3>
            <p>Schedule a visit with us</p>
          </div>
        </Container>
      </section>

      {/* Appointment Form */}
      <section className="page-section" style={{ paddingTop: '48px' }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="appointment-form-wrapper bg-white p-5 shadow-sm rounded">
                {submitted && (
                  <Alert variant="success" className="mt-4">
                    <Alert.Heading>Appointment Booked Successfully!</Alert.Heading>
                    <p>
                      Thank you for booking an appointment with us. We have received your request and 
                      {emailSent ? ' sent a confirmation email to your email address.' : ' will contact you shortly.'}
                    </p>
                  </Alert>
                )}
                {error && <Alert variant="danger">{error}</Alert>}
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your name.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide your phone number.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Treatment</Form.Label>
                        <Form.Select
                          name="treatment"
                          value={formData.treatment}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Treatment</option>
                          {loading ? (
                            <option disabled>Loading treatments...</option>
                          ) : (
                            treatments.map(treatment => (
                              <option key={treatment.id} value={treatment.id}>
                                {treatment.name}
                              </option>
                            ))
                          )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Please select a treatment.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Preferred Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={(e) => {
                            // Get the selected date value
                            const dateValue = e.target.value; // Format: YYYY-MM-DD

                            // Only proceed if a treatment is selected
                            if (!selectedTreatment) {
                              setError('Please select a treatment first before choosing a date.');
                              setTimeout(() => setError(''), 3000);
                              return;
                            }
                            
                            // Only allow the date to be selected if it's available for the treatment
                            if (!isDateDisabled(dateValue)) {
                              handleChange(e);
                              // Clear any existing error
                              if (error) setError('');
                            } else {
                              // Tell the visitor which days this service is available
                              setError(availabilityMessage(selectedTreatment));
                              // Clear the error after 3 seconds
                              setTimeout(() => setError(''), 3000);
                            }
                          }}
                          required
                          min={todayLocal()}
                          disabled={!selectedTreatment} // Disable date selection until a treatment is selected
                        />
                        <Form.Control.Feedback type="invalid">
                          Please select a valid date.
                        </Form.Control.Feedback>
                        {selectedTreatment && (
                          <Form.Text className="text-muted">
                            <strong>Available only on:</strong> {selectedTreatment.availableDays.join(', ')}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Preferred Time</Form.Label>
                        <Form.Select
                          name="time"
                          value={formData.time}
                          onChange={handleChange}
                          required
                          disabled={!selectedTreatment || !formData.date}
                        >
                          <option value="">Select Time</option>
                          {getAvailableTimeSlots().map(time => (
                            <option key={time} value={time}>{time}</option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {!selectedTreatment ? 'Please select a treatment first' : 
                           !formData.date ? 'Please select a date first' : 
                           getAvailableTimeSlots().length === 0 ? 'No available times for selected date' : 
                           'Select your preferred time'}
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          Please select a preferred time.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Information</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please provide any additional information about your condition or specific requirements."
                    />
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 py-2">
                    Book Appointment
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Information Section */}
      <section className="page-section bg-light">
        <Container>
          <Row>
            <Col md={4} className="mb-4">
              <div className="info-card text-center p-4 bg-white shadow-sm rounded h-100">
                <h4 className="mb-3">Contact Us</h4>
                <a
                  href="tel:+14326535487"
                  className="mb-0 d-inline-block text-decoration-none"
                  style={{
                    color: 'var(--primary-color)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '1.25rem',
                    fontWeight: 600
                  }}
                >
                  (432) 653-5487
                </a>
              </div>
            </Col>
            <Col md={4} className="mb-4">
              <div className="info-card text-center p-4 bg-white shadow-sm rounded h-100">
                <h4 className="mb-3">Working Hours</h4>
                <p className="mb-1">Monday - Friday: 9:00 AM - 5:00 PM</p>
              </div>
            </Col>
            <Col md={4} className="mb-4">
              <div className="info-card text-center p-4 bg-white shadow-sm rounded h-100">
                <h4 className="mb-3">Appointment Policy</h4>
                <p className="mb-0">Please arrive 15 minutes before your scheduled appointment.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Appointment;